import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../firebase';
import '../styles/Simulador.css';
import LoadingSpinner from './LoadingSpinner';
import { loadQuizBank } from '../services/rtdbCache';
import { useAuth } from '../hooks/useAuth';
import {
  getCatalog,
  getExamConfig,
  inferInstitucionFromRank,
  isSemar,
  normalizeRank,
  resolveInstitucion,
} from '../config/institutions';

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const ExamTimer = ({ duration, running, onExpire, resetKey }) => {
  const [timeLeft, setTimeLeft] = React.useState(duration);
  const timerRef = React.useRef();
  const expireRef = React.useRef(onExpire);

  React.useEffect(() => {
    expireRef.current = onExpire;
  }, [onExpire]);

  React.useEffect(() => {
    setTimeLeft(duration);
  }, [resetKey, duration]);
  React.useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (expireRef.current) expireRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, resetKey, duration]);
  return <div className="timer">⏰ {formatTime(timeLeft)}</div>;
};

// Definición de jerarquías según SimuladorViewModel.kt
// TODAS las jerarquías tienen distribución 20/20/60, pero con diferentes temas dentro de cada fase
const JERARQUIAS = {
  marinero: { nombre: 'Marinero', search: 'MRO' },
  cabo: { nombre: 'Cabo', search: 'CBO' },
  tercer_maestre: { nombre: 'Tercer Maestre', search: '3RM' },
  segundo_maestre: { nombre: 'Segundo Maestre', search: '2DM' },
  primer_maestre: { nombre: 'Primer Maestre', search: '1RM' },
  teniente_corbeta: { nombre: 'Teniente de Corbeta', search: 'TTE.CORB.' },
  teniente_fragata: { nombre: 'Teniente de Fragata', search: 'TTE.FRAG.' },
  teniente_navio: { nombre: 'Teniente de Navío', search: 'TTE.NAV.' },
  capitan_corbeta: { nombre: 'Capitán de Corbeta', search: 'CAP.CORB.' }
};

const SimuladorExamen = () => {
  const navigate = useNavigate();
  const { institucion: authInstitucion, profileData } = useAuth();
  const institucion = resolveInstitucion(profileData?.institucion || authInstitucion);
  const semar = isSemar(institucion);
  const examConfig = getExamConfig(institucion);
  const hierarchyOptions = semar
    ? Object.entries(JERARQUIAS).map(([key, value]) => ({ key, nombre: value.nombre }))
    : getCatalog(institucion).grados.map((grado) => ({ key: normalizeRank(grado).replace(/\s+/g, '_'), nombre: grado }));
  const getJerarquiaLabel = (key) => {
    if (!key) return 'Ninguna';
    const option = hierarchyOptions.find((item) => item.key === key);
    return option ? option.nombre : String(key).replace(/_/g, ' ');
  };
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [allQuestions, setAllQuestions] = useState([]);
  const [temas, setTemas] = useState([]); // Nuevo: Estado para temas
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [error, setError] = useState(null);
  const [examKey, setExamKey] = useState(0);
  const [reviewQuestions, setReviewQuestions] = useState([]);
  const [selectedJerarquia, setSelectedJerarquia] = useState(null);
  const [showJerarquiaSelector, setShowJerarquiaSelector] = useState(true);
  const [showExitDialog, setShowExitDialog] = useState(false); // Nuevo: Estado para modal de salida
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [personalQuizzes, setPersonalQuizzes] = useState([]);
  const [selectedPersonalQuizIds, setSelectedPersonalQuizIds] = useState(new Set());
  const [personalQuestions, setPersonalQuestions] = useState([]);
  const [resultsFilter, setResultsFilter] = useState('todas'); // 'todas' | 'incorrectas' | 'correctas' | 'revision'
  const [resultsSearch, setResultsSearch] = useState('');
  const timerRef = useRef();
  const touchStartX = useRef(null);

  // Keyboard Navigation Shortcuts
  useEffect(() => {
    if (showJerarquiaSelector || showResults || !questions.length) return;

    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;

      const currentQ = questions[currentQuestion];
      if (!currentQ) return;
      const respuestas = Object.values(currentQ.respuestas || {}).filter(Boolean);

      if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'n') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'p') {
        e.preventDefault();
        handlePrev();
      } else if (e.key.toLowerCase() === 'm' || e.key.toLowerCase() === 'r') {
        e.preventDefault();
        toggleReview(currentQ.id);
      } else {
        const keyUpper = e.key.toUpperCase();
        const letterIndex = ['A', 'B', 'C', 'D', 'E'].indexOf(keyUpper);
        const numberIndex = ['1', '2', '3', '4', '5'].indexOf(e.key);

        const targetIdx = letterIndex !== -1 ? letterIndex : numberIndex;
        if (targetIdx !== -1 && respuestas[targetIdx]) {
          e.preventDefault();
          handleAnswer(currentQ.id, respuestas[targetIdx].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showJerarquiaSelector, showResults, questions, currentQuestion, selectedAnswers, reviewQuestions]);

  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 0) {
      touchStartX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || !e.changedTouches || e.changedTouches.length === 0) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;

    if (Math.abs(diffX) > 60) {
      if (diffX > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  useEffect(() => {
    const auth = getAuth(app);
    return onAuthStateChanged(auth, async (authenticatedUser) => {
      setUser(authenticatedUser);
      if (authenticatedUser) {
        await loadUserProfile(authenticatedUser);
      }
    });
  }, []);

  const loadUserProfile = async (currentUser) => {
    if (!currentUser) return;
    try {
      const dbFirestore = getFirestore(app);
      const userDoc = await getDoc(doc(dbFirestore, 'users', currentUser.uid));

      if (userDoc.exists()) {
        const data = userDoc.data();
        const isAdminUser = data.role === 'admin' ||
          ['admin@ascensos.com', 'fhernando67@gmail.com', 'soporte@ascensos.com', 'ascensosappvortex@gmail.com'].includes(currentUser.email);

        setIsAdmin(isAdminUser);

        // Mapear grado a jerarquía del simulador según la institución del perfil
        const rawGrado = data.grado || data.jerarquia || data.hierarchy || '';
        const userInstitucion = resolveInstitucion(
          data.institucion || inferInstitucionFromRank(rawGrado)
        );
        const mappedKey = isSemar(userInstitucion)
          ? mapGradoToJerarquiaKey(rawGrado)
          : (normalizeRank(rawGrado).replace(/\s+/g, '_') || null);

        console.log('[Simulador] User Profile:', { rawGrado, mappedKey, userInstitucion, isAdminUser });

        if (mappedKey) {
          setSelectedJerarquia(mappedKey);
          // Permitir que se muestre el selector pero filtrado (no ocultarlo automáticamente)
          setShowJerarquiaSelector(true);
        }
      }
    } catch (err) {
      console.error('Error loading user profile for simulator:', err);
    }
  };

  const mapGradoToJerarquiaKey = (grado) => {
    // Normalizar: quitar guiones bajos y puntos por espacios para búsqueda flexible
    const g = grado.toLowerCase().replace(/_/g, ' ').replace(/\./g, ' ');

    if (g.includes('marinero') || g.includes('mro')) return 'marinero';
    if (g.includes('cabo') || g.includes('cbo')) return 'cabo';
    if (g.includes('tercer maestre') || g.includes('3rm') || g.includes('3er')) return 'tercer_maestre';
    if (g.includes('segundo maestre') || g.includes('2dm') || g.includes('2do')) return 'segundo_maestre';
    if (g.includes('primer maestre') || g.includes('1rm') || g.includes('1er')) return 'primer_maestre';
    if (g.includes('teniente corbeta') || g.includes('teniente de corbeta') || g.includes('tte corb') || g.includes('ttc')) return 'teniente_corbeta';
    if (g.includes('teniente fragata') || g.includes('teniente de fragata') || g.includes('tte frag') || g.includes('ttf')) return 'teniente_fragata';
    if (g.includes('teniente navío') || g.includes('teniente navio') || g.includes('teniente de navío') || g.includes('tte nav') || g.includes('ttn')) return 'teniente_navio';
    if (g.includes('capitán corbeta') || g.includes('capitan corbeta') || g.includes('capitán de corbeta') || g.includes('cap corb') || g.includes('ctc')) return 'capitan_corbeta';
    return null;
  };

  // Banco unificado: IndexedDB/caché primero; la red solo se toca al primer uso o al cambiar la versión.
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const dbFirestore = getFirestore(app);
        const bank = await loadQuizBank({ institucion });

        if (bank.preguntas.length === 0) {
          throw new Error('No se encontraron preguntas base en ninguna de las estructuras (preguntas o preguntas_por_tema)');
        }

        const normalizeRespuestas = (resp) => {
          if (!resp) return {};
          if (Array.isArray(resp)) {
            const obj = {};
            resp.forEach((r, idx) => {
              if (r && typeof r === 'object') {
                const rid = r.id !== undefined ? r.id : idx;
                obj[rid] = {
                  id: rid,
                  respuesta: r.respuesta || r.text || '',
                  correcta: r.correcta === true || r.correcta === 1 || r.es_correcta === true || r.es_correcta === 1 || String(r.correcta).toLowerCase() === 'true' || String(r.es_correcta).toLowerCase() === 'true'
                };
              }
            });
            return obj;
          }
          return resp;
        };

        setTemas(bank.temas);
        setAllQuestions(bank.preguntas.map((question) => ({
          ...question,
          respuestas: normalizeRespuestas(question.respuestas)
        })));
        setError(null);

        const userQuizzesRef = collection(dbFirestore, 'user_quizzes', user.uid, 'quizzes');
        const userQuizzesSnap = await getDocs(userQuizzesRef);
        setPersonalQuizzes(userQuizzesSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        })));
      } catch (err) {
        console.error('Error cargando datos:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, institucion]);

  // Cargar preguntas de cuestionarios personales seleccionados
  useEffect(() => {
    if (!user || selectedPersonalQuizIds.size === 0) {
      setPersonalQuestions([]);
      return;
    }

    const fetchPersonalQuestions = async () => {
      const dbFirestore = getFirestore(app);
      let allPersonalQuestions = [];

      try {
        for (const quizId of selectedPersonalQuizIds) {
          const quizData = personalQuizzes.find(q => q.id === quizId);
          const quizAreaId = quizData?.areaId;

          const qRef = collection(dbFirestore, 'user_quizzes', user.uid, 'quizzes', quizId, 'questions');
          const qSnap = await getDocs(qRef);
          const questions = qSnap.docs.map(doc => {
            const data = doc.data();
            const preguntaText = data.pregunta || data.question || '';
            const explicacionText = data.explicacion || data.explanation || '';
            let respuestasMap = {};

            if (data.respuestas && typeof data.respuestas === 'object' && !Array.isArray(data.respuestas)) {
              respuestasMap = data.respuestas;
            } else if (data.respuestas && Array.isArray(data.respuestas)) {
              data.respuestas.forEach((resp, idx) => {
                const respId = resp.id !== undefined ? resp.id : idx;
                respuestasMap[respId] = {
                  id: respId,
                  respuesta: resp.respuesta || resp.text || '',
                  correcta: resp.correcta === true || resp.correcta === 1 || String(resp.correcta) === "1" || String(resp.correcta).toLowerCase() === "true"
                };
              });
            } else {
              const opcionesArray = data.opciones || data.options || [];
              if (Array.isArray(opcionesArray) && opcionesArray.length > 0) {
                const correctIdx = data.correctIndex !== undefined ? data.correctIndex : (data.correctAnswer !== undefined ? data.correctAnswer : 0);
                opcionesArray.forEach((opt, idx) => {
                  respuestasMap[idx] = {
                    id: idx,
                    respuesta: opt,
                    correcta: idx === parseInt(correctIdx)
                  };
                });
              }
            }

            return {
              ...data,
              id: doc.id,
              isPersonal: true,
              quizId: quizId,
              areaId: data.areaId || data.area_id,
              _quizAreaId: quizAreaId,
              pregunta: preguntaText,
              explicacion: explicacionText,
              respuestas: respuestasMap
            };
          });
          allPersonalQuestions = [...allPersonalQuestions, ...questions];

        }
        setPersonalQuestions(allPersonalQuestions);
      } catch (err) {
        console.error("Error cargando preguntas personales:", err);
      }
    };

    fetchPersonalQuestions();
  }, [user, selectedPersonalQuizIds, personalQuizzes]);

  // Filtrado DINÁMICO por Jerarquía + Cuestionarios Personales
  useEffect(() => {
    if (!selectedJerarquia || allQuestions.length === 0 || temas.length === 0) {
      setQuestions([]);
      return;
    }

    // GN/SEDENA: examen completo sobre todas las áreas de su institución.
    if (!semar) {
      const total = getExamConfig(institucion).totalQuestions;
      const target = normalizeRank(selectedJerarquia);
      const temasDelGrado = temas.filter((tema) => {
        if (!tema || !tema.grados) return true;
        const tokens = String(tema.grados).split(',').map((t) => t.trim()).filter(Boolean);
        if (tokens.some((t) => normalizeRank(t) === 'TODOS')) return true;
        return tokens.some((t) => normalizeRank(t) === target);
      });
      const allowedIds = new Set(temasDelGrado.map((tema) => String(tema.id)));
      let pool = allQuestions.filter(
        (q) => q.tema_id !== undefined && q.tema_id !== null && allowedIds.has(String(q.tema_id))
      );
      if (pool.length === 0) pool = allQuestions;

      const shuffledPool = [...pool].sort(() => 0.5 - Math.random());
      const shuffledPersonal = [...personalQuestions].sort(() => 0.5 - Math.random());
      const maxPersonal = Math.min(shuffledPool.length ? Math.floor(total * 0.25) : total, shuffledPersonal.length);
      const selectedPersonal = shuffledPersonal.slice(0, maxPersonal);
      const remaining = Math.max(0, total - selectedPersonal.length);
      const selected = [...selectedPersonal, ...shuffledPool.slice(0, remaining)];

      console.log(`[Simulador] ${institucion}: ${selected.length}/${total} preguntas (${selectedPersonal.length} personales)`);
      if (selected.length < total) {
        console.warn(`[Simulador] ⚠️ Banco insuficiente (${selected.length}/${total}) para ${institucion}.`);
      }

      setQuestions(selected);
      setCurrentQuestion(0);
      setSelectedAnswers({});
      setReviewQuestions([]);
      setShowResults(false);
      setExamKey(k => k + 1);
      return;
    }

    const jerarquiaObj = JERARQUIAS[selectedJerarquia];
    const searchRank = jerarquiaObj.search;

    // 1. Filtrar temas por fase (las 3 áreas se filtran por 'grados' de RTDB)
    const normalizeJerarquia = (str) => {
      return str
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[_\s]+DE[_\s]+/g, '_')
        .trim()
        .replace(/[_\s]+/g, '_');
    };

    const matchesGrado = (tema) => {
      if (!tema.grados) return true;
      const gradosArray = tema.grados.split(',').map(g => normalizeJerarquia(g.trim()));
      const nombreNormalizado = normalizeJerarquia(jerarquiaObj.nombre);
      return gradosArray.includes('TODOS') ||
        gradosArray.includes(searchRank) ||
        gradosArray.includes(searchRank.replace('_', ' ')) ||
        gradosArray.includes(nombreNormalizado);
    };

    // Las 3 áreas se filtran por el campo 'grados' de cada tema en RTDB
    const temasArea1 = temas.filter(t => Number(t.area_id) === 1 && matchesGrado(t));
    const temasArea2 = temas.filter(t => Number(t.area_id) === 2 && matchesGrado(t));
    const temasArea3 = temas.filter(t => Number(t.area_id) === 3 && matchesGrado(t));

    console.log(`[Simulador] Temas - Área1: ${temasArea1.length}, Área2: ${temasArea2.length}, Área3: ${temasArea3.length} (filtrados por grado)`);
    // Diagnóstico: IDs de área 2 vs tema_ids reales en el pool
    const idsArea2 = temasArea2.map(t => String(t.id));
    const temaIdsEnPool = [...new Set(allQuestions.map(q => String(q.tema_id)))].sort();
    const matchesArea2 = temaIdsEnPool.filter(id => idsArea2.includes(id));
    console.log(`[Simulador] 🔎 IDs temas Área2: [${idsArea2.join(',')}]`);
    console.log(`[Simulador] 🔎 tema_ids en pool (muestra): [${temaIdsEnPool.slice(0,20).join(',')}]`);
    console.log(`[Simulador] 🔎 Coincidencias Área2 en pool: [${matchesArea2.join(',')}] (${matchesArea2.length} matches)`);

    // 2. Organizar IDs de temas por fase
    const temasIds = {
      marineros: temasArea1.map(t => String(t.id)),
      legislacion: temasArea2.map(t => String(t.id)),
      especificos: temasArea3.map(t => String(t.id))
    };

    const preguntasPorFase = {
      marineros: allQuestions.filter(q => q.tema_id && temasIds.marineros.includes(String(q.tema_id))),
      legislacion: allQuestions.filter(q => q.tema_id && temasIds.legislacion.includes(String(q.tema_id))),
      especificos: allQuestions.filter(q => q.tema_id && temasIds.especificos.includes(String(q.tema_id)))
    };

    console.log(`[Simulador] Preguntas base - Marineros: ${preguntasPorFase.marineros.length}, Legislación: ${preguntasPorFase.legislacion.length}, Específicos: ${preguntasPorFase.especificos.length}`);

    // 3. Mezclar con preguntas personales según su área
    console.log(`[Simulador] 📋 Preguntas personales cargadas: ${personalQuestions.length}`);
    if (personalQuestions.length > 0) {
      console.log(`[Simulador] 📋 Muestra personal:`, {
        id: personalQuestions[0].id,
        keys: Object.keys(personalQuestions[0]),
        tema_id: personalQuestions[0].tema_id,
        areaId: personalQuestions[0].areaId,
        area_id: personalQuestions[0].area_id,
        area: personalQuestions[0].area
      });
    }

    const matchArea = (q, id, names) => {
      const getAreaNumber = (val) => {
        if (!val) return null;
        if (typeof val === 'number') return val;
        const s = String(val).toLowerCase().trim();
        if (s === '1' || s === 'marinero' || s === 'área 1' || s === 'area 1' || s === 'marineria') return 1;
        if (s === '2' || s === 'legislación' || s === 'legislacion' || s === 'área 2' || s === 'area 2') return 2;
        if (s === '3' || s === 'específicos' || s === 'especificos' || s === 'especialidad' || s === 'specialty' || s === 'área 3' || s === 'area 3') return 3;
        return Number(val);
      };

      // 1. tema_id tiene prioridad absoluta
      if (q.tema_id !== undefined && q.tema_id !== null) {
        const tid = parseInt(q.tema_id);
        const temaMatch = temas.find(t => parseInt(t.id) === tid);
        if (temaMatch) {
          // Tema estándar (1-25) → usar su área
          return Number(temaMatch.area_id) === id;
        }
        // Tema personalizado (fuera de 1-25) → siempre Área 3
        return id === 3;
      }

      // 2. areaId propio de la pregunta (sin herencia del quiz)
      const qArea = getAreaNumber(q.areaId) || getAreaNumber(q.area_id);
      if (qArea === id) return true;

      // 3. Campo 'area' como string
      const areaStr = (q.area || '').toLowerCase().trim();
      if (names.some(n => areaStr.includes(n.toLowerCase()))) return true;

      // 4. areaId del quiz padre (fallback)
      if (q._quizAreaId !== undefined) {
        const parentArea = getAreaNumber(q._quizAreaId);
        if (parentArea === id) return true;
      }

      // 5. Preguntas personales sin información de área → Área 3
      if (q.isPersonal) return id === 3;

      return false;
    };

    const personalMarineros = personalQuestions.filter(q => matchArea(q, 1, ['marinero', 'área 1', 'area 1']));
    const personalLegislacion = personalQuestions.filter(q => matchArea(q, 2, ['legislación', 'legislacion', 'área 2', 'area 2']));
    const personalEspecificos = personalQuestions.filter(q => matchArea(q, 3, ['específicos', 'especificos', 'especialidad', 'área 3', 'area 3']));

    // Función para garantizar la selección de preguntas personales y rellenar con base
    const selectQuestionsForPhase = (baseArr, personalArr, count) => {
      // Barajar las personales primero
      const shuffledPersonal = [...personalArr].sort(() => 0.5 - Math.random());
      
      // Tomar hasta la cantidad deseada de personales
      const selectedPersonal = shuffledPersonal.slice(0, count);
      
      const remainingCount = count - selectedPersonal.length;
      if (remainingCount <= 0) {
        return selectedPersonal;
      }
      
      // Barajar base
      const shuffledBase = [...baseArr].sort(() => 0.5 - Math.random());
      
      // Deduplicar solo por ID de Firebase (no por texto de pregunta).
      // Las preguntas personales importadas comparten el texto con las base,
      // pero tienen IDs distintos → no deben eliminar preguntas del pool base.
      const personalIdSet = new Set(selectedPersonal.map(q => String(q.id)));
      const filteredBase = shuffledBase.filter(q => !personalIdSet.has(String(q.id)));
      
      const selectedBase = filteredBase.slice(0, remainingCount);
      return [...selectedPersonal, ...selectedBase];
    };

    // 4. Selección Aleatoria Priorizada
    let simulatorQuestions;
    if (personalQuestions.length > 0) {
      // Regla 20/20/10/50: base puras en área 1 y 2, 10 de área 3, resto personales
      const area1Sel = selectQuestionsForPhase(preguntasPorFase.marineros, [], 20);
      const area2Sel = selectQuestionsForPhase(preguntasPorFase.legislacion, [], 20);

      const personalToUse = Math.min(personalQuestions.length, 50);
      const baseToUse = 60 - personalToUse;

      const shuffledPersonal = [...personalQuestions].sort(() => 0.5 - Math.random());
      const selectedPersonal = shuffledPersonal.slice(0, personalToUse);

      const shuffledBase = [...preguntasPorFase.especificos].sort(() => 0.5 - Math.random());
      const personalIdSet = new Set(selectedPersonal.map(q => String(q.id)));
      const filteredBase = shuffledBase.filter(q => !personalIdSet.has(String(q.id)));
      const selectedBase = filteredBase.slice(0, baseToUse);

      simulatorQuestions = [...area1Sel, ...area2Sel, ...selectedPersonal, ...selectedBase];
    } else {
      // Regla 20/20/60 estándar (sin personales)
      simulatorQuestions = [
        ...selectQuestionsForPhase(preguntasPorFase.marineros, [], 20),
        ...selectQuestionsForPhase(preguntasPorFase.legislacion, [], 20),
        ...selectQuestionsForPhase(preguntasPorFase.especificos, [], 60)
      ];
    }

    // Validación de integridad
    const marinerosFinal = simulatorQuestions.filter((_, i) => i < 20);
    const legislacionFinal = simulatorQuestions.filter((_, i) => i >= 20 && i < 40);
    const restoFinal = simulatorQuestions.filter((_, i) => i >= 40);
    const personalesEnResto = restoFinal.filter(q => q.isPersonal).length;
    const baseEnResto = restoFinal.length - personalesEnResto;
    console.log(`[Simulador] Preguntas seleccionadas - Marineros: ${marinerosFinal.length}/20, Legislación: ${legislacionFinal.length}/20, Área3+Personales: ${restoFinal.length}/60 (${baseEnResto} base + ${personalesEnResto} personales), Total: ${simulatorQuestions.length}/100`);

    if (simulatorQuestions.length < 100) {
      console.warn(`[Simulador] ⚠️ Simulador incompleto (${simulatorQuestions.length}/100). Revisa la cobertura de temas para esta jerarquía.`);
    }

    setQuestions(simulatorQuestions);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setReviewQuestions([]);
    setShowResults(false);
    setExamKey(k => k + 1);
  }, [selectedJerarquia, allQuestions, temas, personalQuestions, personalQuizzes, semar, institucion]);

  // Temporizador extraído a <ExamTimer /> (evita re-render del árbol completo cada segundo)

  const playSound = (correct) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      if (correct) {
        osc.frequency.setValueAtTime(523, ctx.currentTime);
        osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } else {
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.setValueAtTime(200, ctx.currentTime + 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.type = 'sawtooth';
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      }
    } catch {}
  };

  const handleAnswer = (questionId, answerId) => {
    setSelectedAnswers({ ...selectedAnswers, [questionId]: answerId });
    const pregunta = questions.find(q => q.id === questionId);
    if (pregunta) {
      const respuestas = Object.values(pregunta.respuestas || {});
      const correcta = respuestas.find(r => r.correcta);
      playSound(correcta && answerId === correcta.id);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleFinish = (auto = false) => {
    // Calcular puntaje
    let correct = 0;
    questions.forEach(q => {
      const selected = selectedAnswers[q.id];
      const respuestas = Object.values(q.respuestas || {});
      const correctAnswer = respuestas.find(r => r.correcta);
      // Seguridad ante ID nulo o respuesta malformada
      if (selected && selected === correctAnswer?.id) correct++;
    });
    setScore(correct);
    setShowResults(true);
    clearInterval(timerRef.current);
    if (auto) {
      alert('¡El tiempo se ha agotado! El examen ha finalizado automáticamente.');
    }
  };

  const toggleReview = (questionId) => {
    setReviewQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const goToQuestion = (idx) => {
    setCurrentQuestion(idx);
  };

  const handleConfirmExit = () => {
    setShowExitDialog(false);
    // Reiniciar estados estratégicos para volver al selector
    setSelectedJerarquia(null);
    setShowJerarquiaSelector(true);
    setQuestions([]);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setReviewQuestions([]);
    // Opcional: Si desea salir totalmente del módulo simulador al dashboard principal:
    // navigate('/dashboard'); 
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
  };

  if (loading) {
    return (
      <div className="simulador-container">
        <div className="loading">Accediendo a la base de datos de Vortex...</div>
        <LoadingSpinner label="Cargando datos..." height="300px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="simulador-container">
        <div className="error-message">Error de conexión: {error}</div>
        <button className="simulator-btn-finish" onClick={() => navigate('/dashboard/simulador')} style={{ marginTop: '2rem' }}>Volver al inicio</button>
      </div>
    );
  }

  // Selector de jerarquía antes de empezar
  if (showJerarquiaSelector || !selectedJerarquia) {
    return (
      <div className="simulador-container">
        <h2>Simulador de Exámenes</h2>
        <div className="theme-selector">
          <h3>Selecciona tu jerarquía para el examen:</h3>
          <p className="jerarquia-info">
            {isAdmin ? (
              <>Selector de Administrador: Puedes elegir cualquier jerarquía para pruebas técnicas.</>
            ) : semar ? (
              <>
                El examen se ha configurado automáticamente para tu grado.<br />
                Distribución de 100 preguntas:<br />
                • 20 conocimientos marineros | 20 legislación | 60 específicos
              </>
            ) : (
              <>
                El examen se ha configurado automáticamente para tu grado.<br />
                Examen de {examConfig.totalQuestions} preguntas · {Math.round(examConfig.durationSeconds / 60)} minutos.<br />
                Se evalúan todas las áreas de tu institución.
              </>
            )}
          </p>

          <div className="theme-buttons">
            {hierarchyOptions
              .filter(({ key }) => isAdmin || key === selectedJerarquia)
              .map(({ key, nombre }) => (
                <button
                  key={key}
                  className={`theme-btn ${selectedJerarquia === key ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedJerarquia(key);
                    // setShowJerarquiaSelector(false); // No cerrar para permitir checklist
                  }}
                >
                  {nombre}
                </button>
              ))}
          </div>

          {personalQuizzes.length > 0 && selectedJerarquia && (
            <div className="personal-quizzes-section" style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Mis Cuestionarios Personales</h3>
              <p style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '1rem' }}>Selecciona los cuestionarios que deseas integrar en el examen:</p>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9rem', padding: '4px 12px', background: 'rgba(76,175,80,0.15)', borderRadius: '6px' }}>
                  <input
                    type="checkbox"
                    checked={personalQuizzes.every(q => selectedPersonalQuizIds.has(q.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPersonalQuizIds(new Set(personalQuizzes.map(q => q.id)));
                      } else {
                        setSelectedPersonalQuizIds(new Set());
                      }
                    }}
                  />
                  Seleccionar todos
                </label>
                <button
                  onClick={() => setSelectedPersonalQuizIds(new Set())}
                  style={{ padding: '4px 12px', background: 'rgba(255,68,68,0.15)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#ff4444', fontSize: '0.9rem' }}
                >
                  Deseleccionar todos
                </button>
              </div>
              <div className="personal-quiz-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                {personalQuizzes.map(quiz => (
                  <label key={quiz.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                    <input
                      type="checkbox"
                      checked={selectedPersonalQuizIds.has(quiz.id)}
                      onChange={(e) => {
                        const newSelection = new Set(selectedPersonalQuizIds);
                        if (e.target.checked) newSelection.add(quiz.id);
                        else newSelection.delete(quiz.id);
                        setSelectedPersonalQuizIds(newSelection);
                      }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>{quiz.name} ({quiz.questionCount} reactivos)</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {selectedJerarquia && (
            <button
              className="simulator-btn-finish"
              style={{ marginTop: '2rem', width: '100%', maxWidth: '400px' }}
              onClick={() => {
                const total = examConfig.totalQuestions;
                if (questions.length >= total) {
                  setShowJerarquiaSelector(false);
                } else {
                  const faltantes = total - questions.length;
                  const detalleMsg = questions.length > 0
                    ? `Se encontraron ${questions.length} preguntas, faltan ${faltantes} para completar el examen.\n\nVerifica que hay suficientes preguntas cargadas para tu grado, o selecciona cuestionarios personales adicionales.`
                    : 'No se pudieron cargar preguntas. Verifica tu conexión e intenta de nuevo.';
                  alert(`⚠️ Examen incompleto (${questions.length}/${total})\n\n${detalleMsg}`);
                }
              }}
            >
              🚀 INICIAR SIMULADOR
            </button>
          )}

          <button className="back-button" onClick={() => navigate('/dashboard')} style={{ marginTop: '1rem' }}>
            ← Salir al Panel Principal
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    // Calcular detalles
    let correctas = 0, incorrectas = 0, omitidas = 0;
    let detalles = [];
    try {
      detalles = questions.map((q, idx) => {
        const respuestas = Object.values(q.respuestas || {});
        const correcta = respuestas.find(r => r.correcta);
        const seleccionada = selectedAnswers[q.id];
        const respuestaSeleccionada = respuestas.find(r => r.id === seleccionada);
        let estado = '';

        if (!seleccionada) {
          omitidas++;
          estado = 'Omitida';
        } else if (correcta && seleccionada === correcta.id) {
          correctas++;
          estado = 'Correcta';
        } else {
          incorrectas++;
          estado = 'Incorrecta';
        }
        return {
          id: q.id,
          index: idx + 1,
          pregunta: q.pregunta,
          respuestaSeleccionada: respuestaSeleccionada ? respuestaSeleccionada.respuesta : 'No respondida',
          respuestaCorrecta: correcta ? correcta.respuesta : 'No definida',
          estado,
          isReviewed: reviewQuestions.includes(q.id)
        };
      });
    } catch (e) {
      console.error('[Simulador] Error calculando detalles:', e);
    }

    const filteredDetalles = detalles.filter(d => {
      if (resultsFilter === 'incorrectas' && d.estado !== 'Incorrecta') return false;
      if (resultsFilter === 'correctas' && d.estado !== 'Correcta') return false;
      if (resultsFilter === 'revision' && !d.isReviewed) return false;
      if (resultsSearch.trim()) {
        const query = resultsSearch.toLowerCase();
        return d.pregunta.toLowerCase().includes(query) ||
               d.respuestaSeleccionada.toLowerCase().includes(query) ||
               d.respuestaCorrecta.toLowerCase().includes(query);
      }
      return true;
    });

    return (
      <div className="results-container">
        <h2>¡Simulador finalizado!</h2>
        <div className="results-header">
          <div className="results-score">Puntaje: {score} / {questions.length} ({Math.round((score / (questions.length || 1)) * 100)}%)</div>
          {examConfig.passPercent != null && (
            <div style={{
              marginTop: '0.5rem',
              fontWeight: '700',
              color: Math.round((score / (questions.length || 1)) * 100) >= examConfig.passPercent ? '#4caf50' : '#ff5252'
            }}>
              {Math.round((score / (questions.length || 1)) * 100) >= examConfig.passPercent ? 'Aprobado' : 'No aprobado'} · mínimo {examConfig.passPercent}%
            </div>
          )}
          <div className="results-stats">
            <span className="stat correct">Correctas: {correctas}</span>
            <span className="stat incorrect">Incorrectas: {incorrectas}</span>
            <span className="stat omitted">Omitidas: {omitidas}</span>
          </div>
        </div>

        <div style={{ margin: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setResultsFilter('todas')}
              style={{
                padding: '0.4rem 0.8rem',
                fontSize: '0.78rem',
                borderRadius: '6px',
                border: '1px solid var(--tech-border)',
                background: resultsFilter === 'todas' ? 'var(--gold-matte)' : 'rgba(255,255,255,0.05)',
                color: resultsFilter === 'todas' ? 'var(--navy-deep)' : 'var(--text-primary)',
                fontWeight: resultsFilter === 'todas' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Todas ({detalles.length})
            </button>
            <button
              onClick={() => setResultsFilter('incorrectas')}
              style={{
                padding: '0.4rem 0.8rem',
                fontSize: '0.78rem',
                borderRadius: '6px',
                border: '1px solid #ff5252',
                background: resultsFilter === 'incorrectas' ? 'rgba(255,82,82,0.25)' : 'rgba(255,82,82,0.08)',
                color: '#ffcdd2',
                fontWeight: resultsFilter === 'incorrectas' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Incorrectas ({incorrectas})
            </button>
            <button
              onClick={() => setResultsFilter('correctas')}
              style={{
                padding: '0.4rem 0.8rem',
                fontSize: '0.78rem',
                borderRadius: '6px',
                border: '1px solid #4caf50',
                background: resultsFilter === 'correctas' ? 'rgba(76,175,80,0.25)' : 'rgba(76,175,80,0.08)',
                color: '#a5d6a7',
                fontWeight: resultsFilter === 'correctas' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Correctas ({correctas})
            </button>
            {reviewQuestions.length > 0 && (
              <button
                onClick={() => setResultsFilter('revision')}
                style={{
                  padding: '0.4rem 0.8rem',
                  fontSize: '0.78rem',
                  borderRadius: '6px',
                  border: '1px solid var(--gold-matte)',
                  background: resultsFilter === 'revision' ? 'rgba(197,160,89,0.25)' : 'rgba(197,160,89,0.08)',
                  color: 'var(--gold-light)',
                  fontWeight: resultsFilter === 'revision' ? 'bold' : 'normal',
                  cursor: 'pointer'
                }}
              >
                ★ Revisadas ({reviewQuestions.length})
              </button>
            )}
          </div>

          <input
            type="text"
            placeholder="🔍 Buscar reactivo o respuesta..."
            value={resultsSearch}
            onChange={(e) => setResultsSearch(e.target.value)}
            style={{
              padding: '0.5rem 0.8rem',
              borderRadius: '6px',
              border: '1px solid var(--tech-border)',
              background: 'rgba(10, 26, 42, 0.9)',
              color: 'var(--text-primary)',
              fontSize: '0.85rem',
              width: '100%',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <h3>Detalle de preguntas ({filteredDetalles.length}):</h3>
        <div className="results-list">
          {filteredDetalles.map((d) => (
            <div className="result-card" key={d.id || d.index}>
              <div className="result-question"><b>Pregunta {d.index}:</b> {d.pregunta}</div>
              <div className="result-user-answer">Tu respuesta: <span className={d.estado === 'Correcta' ? 'correct' : d.estado === 'Incorrecta' ? 'incorrect' : 'omitted'}>{d.respuestaSeleccionada}</span></div>
              <div className="result-correct-answer">Respuesta correcta: <span className="correct">{d.respuestaCorrecta}</span></div>
              <div className="result-status">Estado: <b className={d.estado === 'Correcta' ? 'correct' : d.estado === 'Incorrecta' ? 'incorrect' : 'omitted'}>{d.estado}</b> {d.isReviewed && '★'}</div>
            </div>
          ))}
          {filteredDetalles.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#a0aec0', fontStyle: 'italic' }}>
              No se encontraron preguntas con el filtro seleccionado.
            </div>
          )}
        </div>
        <button className="finish-button" onClick={handleConfirmExit}>Volver al inicio del simulador</button>
      </div>
    );
  }
  if (!questions.length) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
      <p>No hay preguntas disponibles para esta selección.</p>
    </div>
  );
  const pregunta = questions[currentQuestion];
  const respuestas = Object.values(pregunta.respuestas || {});
  const progreso = Math.round(((currentQuestion + 1) / questions.length) * 100);
  // Crear navegación compacta por páginas
  const questionsPerPage = 50;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const currentPage = Math.floor(currentQuestion / questionsPerPage);

  const renderQuickNav = () => {
    const startIdx = currentPage * questionsPerPage;
    const endIdx = Math.min(startIdx + questionsPerPage, questions.length);
    const pageQuestions = questions.slice(startIdx, endIdx);

    return (
      <div className="quick-nav-container">
        {/* Navegación por páginas */}
        <div className="page-nav">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`page-btn ${i === currentPage ? 'active' : ''}`}
              onClick={() => goToQuestion(i * questionsPerPage)}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Barra de navegación rápida por preguntas de la página actual */}
        <div className="quick-nav-bar">
          {pageQuestions.map((q, idx) => {
            const globalIdx = startIdx + idx;
            const isAnswered = selectedAnswers[q.id] !== undefined;
            const isReviewed = reviewQuestions.includes(q.id);
            return (
              <button
                key={q.id}
                className={`quick-nav-btn ${globalIdx === currentQuestion ? 'active' : ''} ${isAnswered ? 'answered' : ''} ${isReviewed ? 'reviewed' : ''}`}
                onClick={() => goToQuestion(globalIdx)}
                title={`${isReviewed ? '★ ' : ''}Pregunta ${globalIdx + 1}${isAnswered ? ' (Respondida)' : ''}`}
              >
                {globalIdx + 1}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="simulador-container">
      {renderQuickNav()}
      <div className="quiz-header">
        <div className="header-left">
          <button className="back-button" onClick={() => setShowExitDialog(true)}>← Volver</button>
          <button className="theme-change-btn" onClick={() => setShowJerarquiaSelector(true)}>
            Cambiar Jerarquía ({getJerarquiaLabel(selectedJerarquia)})
          </button>
        </div>
        <div className="quiz-stats">
          <div>Pregunta {currentQuestion + 1} / {questions.length}</div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progreso}%` }}></div>
          </div>
        </div>
        <ExamTimer
          duration={examConfig.durationSeconds}
          running={!showResults && !loading}
          onExpire={() => handleFinish(true)}
          resetKey={examKey}
        />
      </div>
      <div className="question-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="question-text">{pregunta.pregunta}</div>
        <div className="simulator-options-grid">
          {respuestas.filter(Boolean).map((r, idx) => {
            const letter = String.fromCharCode(65 + idx);
            const isSelected = selectedAnswers[pregunta.id] === r.id;
            return (
              <button
                key={r.id || idx}
                className={`simulator-answer-btn${isSelected ? ' selected' : ''}`}
                onClick={() => handleAnswer(pregunta.id, r.id)}
              >
                <span className="option-letter">{letter}</span>
                <span className="option-text">{r.respuesta}</span>
              </button>
            );
          })}
        </div>
        <div className="review-row">
          <button className={`review-btn${reviewQuestions.includes(pregunta.id) ? ' marked' : ''}`} onClick={() => toggleReview(pregunta.id)}>
            {reviewQuestions.includes(pregunta.id) ? 'Quitar marca de revisión ★' : 'Marcar para revisar ★'}
          </button>
        </div>
        <div className="simulator-nav-controls">
          <button className="simulator-btn-nav" onClick={handlePrev} disabled={currentQuestion === 0}>Anterior</button>
          {currentQuestion < questions.length - 1 ? (
            <button className="simulator-btn-nav" onClick={handleNext} disabled={selectedAnswers[pregunta.id] === undefined}>Siguiente</button>
          ) : (
            <button
              className="simulator-btn-finish"
              onClick={() => handleFinish(false)}
              disabled={Object.keys(selectedAnswers).length < questions.length}
            >
              Finalizar ({Object.keys(selectedAnswers).length}/{questions.length})
            </button>
          )}
        </div>
      </div>

      {/* Modal de Confirmación de Salida */}
      {showExitDialog && (
        <div className="simulator-modal-overlay">
          <div className="simulator-modal-content">
            <div className="modal-icon">⚠️</div>
            <h3>¿Abandonar el Examen?</h3>
            <p>Estás a punto de salir del simulador. Todo tu progreso actual se borrará y los resultados no se guardarán.</p>
            <div className="modal-actions-vortex">
              <button className="modal-btn-cancel" onClick={() => setShowExitDialog(false)}>Continuar Examen</button>
              <button className="modal-btn-confirm" onClick={handleConfirmExit}>Salir y Perder Progreso</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimuladorExamen; 